/**
 * Utility functions for formatting user-friendly error messages
 */

interface ErrorMessageMapping {
  patterns: string[];
  message: string;
}

/**
 * Error message mappings for ClickHouse connectivity issues
 * Each mapping contains patterns to match and a user-friendly message
 */
const CLICKHOUSE_ERROR_MAPPINGS: ErrorMessageMapping[] = [
  {
    patterns: ['Circuit breaker is OPEN', 'Circuit breaker', 'temporarily unavailable'],
    message: 'ClickHouse service is temporarily unavailable. The system will retry automatically.',
  },
  {
    patterns: ['timed out', 'timeout'],
    message: 'ClickHouse query timed out. The system will retry automatically.',
  },
  {
    patterns: ['EADDRNOTAVAIL', 'ECONNREFUSED', 'ECONNRESET', 'connection error'],
    message: 'ClickHouse connection error. The system will retry automatically.',
  },
  {
    patterns: ['socket hang up', 'connection lost', 'ETIMEDOUT'],
    message: 'Connection to ClickHouse was lost. The system will retry automatically.',
  },
];

/**
 * Transforms a raw error message into a user-friendly message
 * Specifically handles ClickHouse connectivity issues
 *
 * @param rawMessage - The raw error message from the API or exception
 * @returns A user-friendly error message
 */
export function formatClickHouseErrorMessage(rawMessage: string): string {
  if (!rawMessage) {
    return 'An unexpected error occurred. Please try again.';
  }

  for (const mapping of CLICKHOUSE_ERROR_MAPPINGS) {
    if (mapping.patterns.some(pattern => rawMessage.includes(pattern))) {
      return mapping.message;
    }
  }

  // Return original message if no mapping matches
  return rawMessage;
}

/**
 * Extracts error message from various error formats (axios, standard Error, etc.)
 *
 * @param error - The error object (can be axios error, standard Error, or unknown)
 * @param fallbackMessage - Message to use if no error message can be extracted
 * @returns The extracted error message
 */
export function extractErrorMessage(error: any, fallbackMessage: string = 'An unexpected error occurred'): string {
  // Axios error format
  if (error?.response?.data?.error) {
    return error.response.data.error;
  }

  // Axios error with message in data
  if (error?.response?.data?.message) {
    return error.response.data.message;
  }

  // Standard Error object
  if (error?.message) {
    return error.message;
  }

  // String error
  if (typeof error === 'string') {
    return error;
  }

  return fallbackMessage;
}

/**
 * Combines error extraction and ClickHouse-specific formatting
 * Use this for handling errors in components that interact with ClickHouse
 *
 * @param error - The error object
 * @param fallbackMessage - Message to use if no error message can be extracted
 * @returns A user-friendly, formatted error message
 */
export function formatApiErrorMessage(error: any, fallbackMessage: string = 'An unexpected error occurred'): string {
  const rawMessage = extractErrorMessage(error, fallbackMessage);
  return formatClickHouseErrorMessage(rawMessage);
}
